import { useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { errText } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Btn, inputCls, useOps } from "./ui";
import { ACCEPT, buildRows, cleanName, expandFiles, kindOk } from "./bulkMatch";

const label = (slots, k) => slots.find((s) => s.slot === k)?.label || k;
const STATUS = { pending: "text-slate-500", uploading: "text-amber-300", done: "text-green-400", error: "text-red-400", skipped: "text-slate-500" };

function DropZone({ accept, onFiles, busy }) {
  const [over, setOver] = useState(false);
  const input = useRef(null);
  const drop = (e) => { e.preventDefault(); setOver(false); if (e.dataTransfer.files?.length) onFiles([...e.dataTransfer.files]); };
  return (
    <div data-testid="bulk-dropzone" onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)} onDrop={drop}
      onClick={() => input.current?.click()}
      className={`cursor-pointer rounded border-2 border-dashed px-4 py-8 text-center text-xs transition-colors ${over ? "border-sky-400 bg-sky-500/10" : "border-[#243144] hover:border-sky-700"}`}>
      {busy ? "Reading files…" : "Drop files or one .zip here, or click to choose (multi-select)"}
      <input ref={input} type="file" multiple accept={`${accept},.zip,application/zip`} className="hidden" data-testid="bulk-file-input"
        onChange={(e) => { if (e.target.files?.length) onFiles([...e.target.files]); e.target.value = ""; }} />
    </div>
  );
}

function Row({ r, slots, used, setSlot }) {
  const spec = slots.find((s) => s.slot === r.slot);
  const options = slots.filter((s) => kindOk(r.type, s.kind) && (!used.has(s.slot) || s.slot === r.slot));
  return (
    <tr data-testid={`bulk-row-${r.id}`} className="border-b border-[#243144] last:border-0">
      <td className="max-w-[260px] truncate py-1 pr-2 text-slate-200" title={r.file.name}>{r.file.name}</td>
      <td className="py-1 pr-2">
        {r.auto ? <span data-testid={`bulk-row-slot-${r.id}`} className="text-sky-300">{label(slots, r.slot)}</span> : (
          <select data-testid={`bulk-assign-${r.id}`} className={`${inputCls} py-0.5 text-xs`} value={r.slot} onChange={(e) => setSlot(r.id, e.target.value)}>
            <option value="">— {r.issue || "not assigned"} —</option>
            {options.map((s) => <option key={s.slot} value={s.slot}>{s.label}</option>)}
          </select>
        )}
      </td>
      <td className="py-1 text-[10px]">
        {spec?.filled && r.status === "pending" && <span className="text-amber-300">has upload</span>}
        <span data-testid={`bulk-row-status-${r.id}`} className={`ml-1 uppercase ${STATUS[r.status]}`}>{r.status}{r.error ? `: ${r.error}` : ""}</span>
      </td>
    </tr>
  );
}

export default function BulkUpload({ title, slots, onDone }) {
  const { api } = useOps();
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [replace, setReplace] = useState(false);
  const [reading, setReading] = useState(false);
  const [running, setRunning] = useState(false);
  const kinds = [...new Set(slots.map((s) => s.kind))];
  const accept = kinds.map((k) => ACCEPT[k]).join(",");

  const used = useMemo(() => new Set(rows.map((r) => r.slot).filter(Boolean)), [rows]);
  const matched = rows.filter((r) => r.slot);
  const unmatched = rows.filter((r) => !r.slot);
  const willUpload = matched.filter((r) => replace || !slots.find((s) => s.slot === r.slot)?.filled);
  const stillEmpty = slots.filter((s) => !s.filled && !used.has(s.slot));

  const addFiles = async (list) => {
    setReading(true);
    try { setRows(buildRows(await expandFiles(list), slots)); }
    catch (e) { toast.error(`Could not read files: ${e.message}`); } finally { setReading(false); }
  };
  const setSlot = (id, slot) => setRows((rs) => rs.map((r) => (r.id === id ? { ...r, slot } : r)));
  const patch = (id, p) => setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...p } : r)));

  const confirm = async () => {
    setRunning(true);
    const queue = [...willUpload];
    matched.filter((r) => !willUpload.includes(r)).forEach((r) => patch(r.id, { status: "skipped", error: "already uploaded" }));
    let ok = 0, bad = 0;
    const worker = async () => {
      for (let r = queue.shift(); r; r = queue.shift()) {
        patch(r.id, { status: "uploading" });
        const fd = new FormData();
        fd.append("file", new File([r.file], cleanName(r.file.name), { type: r.type }));
        try { await api.post(`/content/${r.slot}`, fd); patch(r.id, { status: "done" }); ok += 1; }
        catch (e) { patch(r.id, { status: "error", error: errText(e) }); bad += 1; }
      }
    };
    await Promise.all([worker(), worker(), worker()]);
    setRunning(false);
    toast[bad ? "error" : "success"](`${ok} uploaded${bad ? `, ${bad} failed` : ""}`);
    onDone();
  };
  const close = (v) => { if (running) return; setOpen(v); if (!v) { setRows([]); setReplace(false); } };
  const finished = !running && rows.some((r) => ["done", "error", "skipped"].includes(r.status)) && rows.every((r) => !r.slot || r.status !== "pending");

  return (
    <>
      <Btn onClick={() => setOpen(true)} disabled={!slots.length} data-testid={`bulk-upload-open-${title.split(" ")[0].toLowerCase()}`}>Bulk upload</Btn>
      <Dialog open={open} onOpenChange={close}>
        <DialogContent className="ops max-h-[90vh] max-w-4xl overflow-auto border-[#243144] bg-[#131a26] text-slate-100" data-testid="bulk-upload-dialog">
          <DialogHeader><DialogTitle className="text-sm">Bulk upload — {title}</DialogTitle></DialogHeader>
          {!rows.length ? <DropZone accept={accept} onFiles={addFiles} busy={reading} /> : (
            <div className="space-y-3 text-xs">
              <div data-testid="bulk-summary" className="flex flex-wrap gap-4 text-slate-300">
                <span>{rows.length} files</span><span className="text-sky-300">{matched.length} matched</span>
                <span className={unmatched.length ? "text-amber-300" : ""}>{unmatched.length} unmatched</span>
                <span>{stillEmpty.length} slots still empty after this</span>
              </div>
              <table className="w-full" data-testid="bulk-match-table"><tbody>
                {[...matched, ...unmatched].map((r) => <Row key={r.id} r={r} slots={slots} used={used} setSlot={setSlot} />)}
              </tbody></table>
              {stillEmpty.length > 0 && (
                <div data-testid="bulk-empty-slots" className="text-slate-500">Still empty: {stillEmpty.map((s) => s.label).join(" · ")}</div>
              )}
              <div className="flex flex-wrap items-center gap-3 border-t border-[#243144] pt-3">
                <label className="flex items-center gap-2"><input type="checkbox" data-testid="bulk-replace-existing" checked={replace} disabled={running} onChange={(e) => setReplace(e.target.checked)} /> Replace existing uploads</label>
                <span className="text-slate-500">{willUpload.length} will upload{matched.length - willUpload.length ? `, ${matched.length - willUpload.length} skipped (already uploaded)` : ""}</span>
                <Btn variant="ghost" className="ml-auto" disabled={running} onClick={() => setRows([])} data-testid="bulk-start-over">Start over</Btn>
                {finished ? <Btn onClick={() => close(false)} data-testid="bulk-close">Close</Btn>
                  : <Btn onClick={confirm} disabled={running || !willUpload.length} data-testid="bulk-confirm-upload">{running ? "Uploading…" : "Confirm upload"}</Btn>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
